import { CSSProperties, ReactNode } from 'react';
import cn from 'classnames';
import { ScheduleStatusMode, ShiftEdgeKind } from '@lp/features/Scheduler/types';
import { CSShiftEdgeColors } from '@lp/features/Scheduler/SchedulerClinicalSpecialists/types';

const STRIPE_PX = 2.144;

// Diagonal stripes fade in from the previous side, becoming opaque toward the current (next) side.
const STRIPE_FADE = 'linear-gradient(to right, transparent, #000)';
// Dash patterns for planning borders — `border-image` can't dash, so gradient lines are masked instead.
const DASH_H = 'repeating-linear-gradient(to right, #000 0 3px, transparent 3px 6px)';
const DASH_V = 'repeating-linear-gradient(to bottom, #000 0 3px, transparent 3px 6px)';

const FADE_OVERLAY = 'rgba(255, 255, 255, 0.4)';

type CSShiftEdgePatchProps = {
  kind: ShiftEdgeKind;
  variant: ScheduleStatusMode;
  /** Colours of the previous-day shift (left side of the boundary). */
  prev: CSShiftEdgeColors;
  /** Colours of this (current) shift (right side of the boundary). */
  current: CSShiftEdgeColors;
  /** Dim the previous side when it belongs to another period. */
  prevLighten?: boolean;
  /** Dim the current side when it belongs to another period. */
  currentLighten?: boolean;
};

// Out-of-period shifts are dimmed with a white overlay (`rgba(255,255,255,0.4)` == `color-mix(color
// 60%, white)`) covering only the out-of-period half, with a hard edge at the period boundary (patch
// midpoint), so the fade is a sharp overlay instead of being blended into the patch.
const getFadeOverlay = (prevLighten?: boolean, currentLighten?: boolean): string | undefined => {
  if (prevLighten && currentLighten) return FADE_OVERLAY;
  if (prevLighten) return `linear-gradient(to right, ${FADE_OVERLAY} 0 50%, transparent 50%)`;
  if (currentLighten) return `linear-gradient(to right, transparent 50%, ${FADE_OVERLAY} 50%)`;
  return undefined;
};

const stripesBackground = (color: string) =>
  `repeating-linear-gradient(-45deg, ${color} 0 ${STRIPE_PX}px, transparent ${STRIPE_PX}px ${STRIPE_PX * 2}px)`;

type Edge = 'top' | 'bottom' | 'left' | 'right';

const EDGE_CLASS: Record<Edge, string> = {
  top: 'top-0 inset-x-0 h-px',
  bottom: 'bottom-0 inset-x-0 h-px',
  left: 'left-0 inset-y-0 w-px',
  right: 'right-0 inset-y-0 w-px',
};

// Horizontal edges use the prev→next gradient (solid when both colours match); vertical edges take
// the solid colour of the side they sit on.
const getBorderBackground = (edge: Edge, prev: string, next: string): string => {
  if (edge === 'left') return prev;
  if (edge === 'right') return next;
  return prev === next ? next : `linear-gradient(90deg, ${prev}, ${next})`;
};

// Planning masks each line into dashes since `border-image` can't be dashed.
const getBorderMask = (isHorizontal: boolean, dashed: boolean): string | undefined => {
  if (!dashed) return undefined;
  return isHorizontal ? DASH_H : DASH_V;
};

// Renders 1px border lines: horizontal edges use the prev→next gradient, vertical edges the solid
// prev/next colour. Planning masks each line into dashes since `border-image` can't be dashed.
const renderBorders = (edges: Edge[], prev: string, next: string, dashed: boolean): ReactNode =>
  edges.map((edge) => {
    const isHorizontal = edge === 'top' || edge === 'bottom';
    const background = getBorderBackground(edge, prev, next);
    const mask = getBorderMask(isHorizontal, dashed);

    return (
      <div
        key={edge}
        className={cn('absolute', EDGE_CLASS[edge])}
        style={{ background, maskImage: mask, WebkitMaskImage: mask }}
      />
    );
  });

const PATCH_CLASS = 'absolute left-[7px] top-0 z-1 w-10 h-full -translate-x-full pointer-events-none';

/**
 * Renders the gap/overlap marking between a shift and its previous-day neighbour, owned by the
 * later (current) shift and drawn at its leading edge. Styling follows the schedule state:
 *
 * - `published` — overlap is a previous→current gradient with next-colour stripes; gap is a white
 *   block with a solid gradient border. Same-colour overlap stripes darken by −20% lightness.
 * - `preSchedule` / `planning` — overlap is a solid previous-shift background with uniform
 *   next-background stripes and a top/bottom gradient border (solid for pre-schedule, dashed for
 *   planning); gap is a white block with the same border treatment. Same-colour stripes darken by
 *   −10% lightness.
 *
 * Out-of-period shifts are dimmed by a white overlay covering only their half of the patch, with a
 * hard edge at the period boundary (patch midpoint). Colours resolve from `getCSShiftEdgeColors`.
 */
export const CSShiftEdgePatch = ({
  kind,
  variant,
  prev,
  current,
  prevLighten,
  currentLighten,
}: CSShiftEdgePatchProps) => {
  const isPublished = variant === ScheduleStatusMode.Published;
  const dashed = variant === ScheduleStatusMode.Planning;
  const isSameColor = prev.color === current.color;
  const overlayBg = getFadeOverlay(prevLighten, currentLighten);
  const overlay = overlayBg ? <div className="absolute inset-0" style={{ background: overlayBg }} /> : null;

  if (kind === ShiftEdgeKind.Gap) {
    if (isPublished) {
      const style: CSSProperties = {
        border: '1px solid transparent',
        ...(isSameColor
          ? { borderColor: current.color }
          : { borderImage: `linear-gradient(90deg, ${prev.color}, ${current.color}) 1` }),
      };

      return (
        <div aria-hidden style={style} className={cn(PATCH_CLASS, 'bg-white')}>
          {overlay}
        </div>
      );
    }

    return (
      <div aria-hidden className={cn(PATCH_CLASS, 'bg-white')}>
        {renderBorders(['top', 'bottom', 'left', 'right'], prev.border, current.border, dashed)}
        {overlay}
      </div>
    );
  }

  // Diagonal stripes in the next shift's colour — a darker shade when both shifts match so they stay
  // visible. Published uses the saturated identity colour and fades the stripes toward the current
  // side; pre-schedule/planning use the lighter cell background as uniform (unfaded) stripes.
  const darkenBy = isPublished ? 20 : 10;
  const stripeBase = isPublished ? current.color : current.bg;
  const stripeColor = isSameColor ? `hsl(from ${stripeBase} h s calc(l - ${darkenBy}))` : stripeBase;
  const stripeMask = isPublished ? STRIPE_FADE : undefined;
  const stripes = (
    <div
      className="absolute inset-0"
      style={{ background: stripesBackground(stripeColor), maskImage: stripeMask, WebkitMaskImage: stripeMask }}
    />
  );

  if (isPublished) {
    // Published: previous→current gradient base with the stripes fading in toward the current side.
    const background = `linear-gradient(to left, ${current.color} 0%, ${prev.color} 55%)`;

    return (
      <div aria-hidden className={PATCH_CLASS} style={{ background }}>
        {stripes}
        {overlay}
      </div>
    );
  }

  // Pre-schedule / planning: solid previous-shift background + uniform stripes + top/bottom border.
  return (
    <div aria-hidden className={PATCH_CLASS} style={{ background: prev.bg }}>
      {stripes}
      {renderBorders(['top', 'bottom'], prev.border, current.border, dashed)}
      {overlay}
    </div>
  );
};
